import React from "react"
import { Microphone, MicrophoneSlash, Pause, Play, Shuffle, PhoneSlash, Keyboard } from "@phosphor-icons/react"
import { cn } from "../../../lib/utils"

interface CallControlsProps {
  muted: boolean
  onHold: boolean
  onToggleMute: () => void
  onToggleHold: () => void
  onTransfer: () => void
  onEndCall: () => void
  onKeypad: () => void
}

interface ControlButtonProps {
  icon: React.ReactNode
  activeIcon?: React.ReactNode
  label: string
  active?: boolean
  variant?: "default" | "danger"
  onClick: () => void
}

function ControlButton({
  icon,
  activeIcon,
  label,
  active,
  variant = "default",
  onClick,
}: ControlButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex flex-col items-center gap-0.5 py-1.5 px-2 rounded-lg",
        "transition-all duration-200 min-w-0 flex-1",
        variant === "danger"
          ? "bg-danger/20 hover:bg-danger/30 text-danger"
          : active
          ? "bg-warning/20 text-warning"
          : "bg-white/10 hover:bg-white/15 text-white/80 hover:text-white"
      )}
    >
      <div className="h-4 w-4 flex items-center justify-center">
        {active && activeIcon ? activeIcon : icon}
      </div>
      <span className="text-[8px] font-medium">{label}</span>
    </button>
  )
}

export function CallControls({
  muted,
  onHold,
  onToggleMute,
  onToggleHold,
  onTransfer,
  onEndCall,
  onKeypad,
}: CallControlsProps) {
  return (
    <div className="space-y-1.5">
      {/* Row 1: Mute, Hold, Transfer */}
      <div className="flex gap-1">
        <ControlButton
          icon={<Microphone className="h-3.5 w-3.5" />}
          activeIcon={<MicrophoneSlash className="h-3.5 w-3.5" />}
          label={muted ? "Unmute" : "Mute"}
          active={muted}
          onClick={onToggleMute}
        />
        <ControlButton
          icon={<Pause className="h-3.5 w-3.5" />}
          activeIcon={<Play className="h-3.5 w-3.5" />}
          label={onHold ? "Resume" : "Hold"}
          active={onHold}
          onClick={onToggleHold}
        />
        <ControlButton
          icon={<Shuffle className="h-3.5 w-3.5" />}
          label="Transfer"
          onClick={onTransfer}
        />
      </div>
      {/* Row 2: Keypad, End */}
      <div className="flex gap-1.5">
        <ControlButton
          icon={<Keyboard className="h-3.5 w-3.5" />}
          label="Keypad"
          onClick={onKeypad}
        />
        <ControlButton
          icon={<PhoneSlash className="h-3.5 w-3.5" />}
          label="End"
          variant="danger"
          onClick={onEndCall}
        />
      </div>
    </div>
  )
}
